import type React from 'react';
import type { RecipeTone, ReviewRecipe, ReviewRecipeGroup } from './IReviewRecipe';

/**
 * IView — the single, unifying registration for "a way of looking at the
 * diagram." It subsumes the three mechanisms a domain used to expose
 * separately (a viewpoint perspective, a lens field-pack chip, and a
 * composite recipe): each of those now simply registers an `IView`. The
 * user sees ONE labelled list ("Views") and one hero ("View Box"); the
 * mechanism behind a given view is an implementation detail.
 *
 * Named by OUTCOME / the question it answers ("Highlight overloaded
 * managers", "Where is unowned production?"), not by mechanism. Modelled on
 * the Compass `LiveReviewView` catalogue — the gold-standard picker — lifted
 * into shared infra so every domain offers the same comfy UX with its own,
 * use-case-relevant views.
 */
export interface IView {
  id: string;
  /** Outcome-first title — the question this view answers. */
  title: string;
  /** One-line plain-language "what it does". */
  description: string;
  icon: React.ElementType;
  tone: RecipeTone;
  /** Concern grouping for the gallery ("Security", "Structure", "Health"). */
  group?: string;
  /** Persona/use-case tags — drive persona-default selection + recommendations. */
  personaTags?: readonly string[];
  /** Entitlement gate; non-entitled plans render the card visible-but-locked. */
  entitlementKey?: string;
  /** Apply the view — compose this domain's filters/lenses/chips/zoom/panel. */
  apply: () => void;
  /** Optional teardown when another view is chosen or this one is cleared. */
  clear?: () => void;
  /** Whether the view's effects are currently in force (see ReviewRecipe.isApplied). */
  isApplied?: () => boolean;
  /** Optional trailing badge (e.g. "Live", a data-availability marker). */
  badge?: React.ReactNode;
}

/** A titled group of views for the sectioned gallery. */
export interface IViewGroup {
  heading?: string;
  views: readonly IView[];
}

/** Flatten grouped views to a single list. */
export const flattenViews = (groups: readonly IViewGroup[]): IView[] =>
  groups.flatMap((g) => [...g.views]);

/** Resolve a view by id across grouped or flat lists. */
export const findViewById = (
  groups: readonly IViewGroup[],
  id: string | null | undefined,
): IView | null => {
  if (!id) return null;
  for (const group of groups) {
    const match = group.views.find((v) => v.id === id);
    if (match) return match;
  }
  return null;
};

/**
 * Adapt an `IView` to the shared `ReviewRecipe` card model so the gallery
 * reuses the exact card renderer (`RecipeSection`) — one aesthetic, one
 * place. `accessory` defaults to the view's badge; callers may override
 * (e.g. a lock badge for gated views).
 */
export const viewToRecipe = (
  view: IView,
  accessory?: React.ReactNode,
): ReviewRecipe => ({
  id: view.id,
  title: view.title,
  description: view.description,
  icon: view.icon,
  tone: view.tone,
  apply: view.apply,
  clear: view.clear,
  accessory: accessory ?? view.badge,
});

/** Adapt grouped views to recipe groups, with an optional per-view accessory. */
export const viewGroupsToRecipeGroups = (
  groups: readonly IViewGroup[],
  accessoryFor?: (view: IView) => React.ReactNode,
): ReviewRecipeGroup[] =>
  groups.map((group) => ({
    heading: group.heading,
    recipes: group.views.map((view) => viewToRecipe(view, accessoryFor?.(view))),
  }));
